import React, { useEffect } from "react";
import { Dialog as TwDialog, Button, Input, Spinner } from "@material-tailwind/react";
import { observer } from "mobx-react-lite";

import { AddressDTO } from "../../../Common/DTOs/Address/AddressDTO";
import { countryController } from "../../../Common/Controllers/CountryController";
import { addressDialogController } from "../Controllers/AddressDialogController";
import { StateCode } from "../../../Common/DTOs/Address/StateCode";

type Props = {
  open: boolean;
  onClose: () => void;
  initialData?: AddressDTO;
  onSubmit: (data: AddressDTO) => Promise<void>;
};

export const AddressDialog = observer(({ open, onClose, initialData, onSubmit }: Props) => {
  const ctrl = addressDialogController;
  const { formData, selectedCountryName, loading, submitting } = ctrl;
  const { countries, isLoading } = countryController;

  useEffect(() => {
    ctrl.initialize(open ? initialData : undefined);
  }, [open, initialData]);

  const handleSave = async () => {
    await ctrl.submit(onSubmit, onClose);
  };

  return (
    <TwDialog
      open={open}
      handler={onClose}
      className="bg-main/90 !h-[100dvh] text-light-grey border border-light-grey rounded-none md:!rounded-xl shadow-2xl p-2 md:p-6
      max-h-[800px] m-0 md:m-4 max-w-full md:!max-w-[680px] lg:!max-w-[800px] xl:!max-w-[1000px] 2xl:!max-w-[1280px] !overflow-y-hidden
      flex flex-col"
    >
      <div className="flex-none border-b border-light-grey/40 flex flex-col items-center justify-center text-center pt-4 pb-6">
        <span className="text-lg md:text-4xl font-bold">
          {initialData ? "Edit Address" : "New Address"}
        </span>
      </div>

      <div className="flex-auto overflow-y-auto p-4 pb-0 md:p-12">
        {loading ? (
          <div className="flex justify-center items-center h-full">
            <Spinner color="purple" />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6 h-full">
            {!formData.isBillingAddress &&
              <Input
                label="Description"
                className="bg-main text-white border-light-grey md:text-lg"
                labelProps={{ className: "!text-white" }}
                value={formData.description}
                onChange={(e) => ctrl.setValue("description", e.target.value)}
                error={ctrl.descriptionError}
                required
              />
            }

            <Input
              label="Company Name"
              className="bg-main text-white border-light-grey md:text-lg"
              labelProps={{ className: "!text-white" }}
              value={formData.companyName}
              onChange={(e) => ctrl.setValue("companyName", e.target.value)}
              error={ctrl.companyNameError}
              required
            />

            <Input
              label="First Name"
              className="bg-main text-white border-light-grey md:text-lg"
              labelProps={{ className: "!text-white" }}
              value={formData.firstName}
              onChange={(e) => ctrl.setValue("firstName", e.target.value)}
              error={ctrl.firstNameError}
              required
            />

            <Input
              label="Surname"
              className="bg-main text-white border-light-grey md:text-lg"
              labelProps={{ className: "!text-white" }}
              value={formData.surname}
              onChange={(e) => ctrl.setValue("surname", e.target.value)}
              error={ctrl.surnameError}
              required
            />

            <div className="w-full">
              <label className="block text-sm mb-2 text-white">Country</label>
              {isLoading ? (
                <div className="text-gray-300 animate-fadeIn">
                  <Spinner />
                </div>
              ) : (
                <select
                  value={selectedCountryName}
                  required
                  onChange={(e) => ctrl.handleCountryChange(e.target.value)}
                  className={`w-full p-2 bg-main rounded-md border ${ctrl.countryError ? "border-red-500" : "border-light-grey"
                    } text-white animate-fadeIn`}
                >
                  {countries.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <Input
              label="Address 1"
              className="bg-main text-white border-light-grey md:text-lg"
              labelProps={{ className: "!text-white" }}
              value={formData.address1}
              onChange={(e) => ctrl.setValue("address1", e.target.value)}
              required
              error={ctrl.address1Error}
            />

            <Input
              label="Address 2"
              className="bg-main text-white border-light-grey md:text-lg"
              labelProps={{ className: "!text-white" }}
              value={formData.address2}
              onChange={(e) => ctrl.setValue("address2", e.target.value)}
              error={ctrl.address2Error}
            />

            <Input
              label="City"
              className="bg-main text-white border-light-grey md:text-lg"
              labelProps={{ className: "!text-white" }}
              value={formData.address3 ?? ""}
              onChange={(e) => ctrl.setValue("address3", e.target.value)}
              required
              error={ctrl.address3Error}
            />

            {formData.country === "United States" ? (
              <select
                value={formData.address4}
                required
                onChange={(e) => ctrl.setValue("address4", e.target.value)}
                className={`w-full p-2 bg-main rounded-md border ${ctrl.address4Error ? "border-red-500" : "border-light-grey"
                  } text-white animate-fadeIn`}
              >
                <option value="">Select State *</option>
                {Object.entries(StateCode).map(([key, value]) => (
                  typeof value === "number" ? null : (
                    <option key={key} value={value}>
                      {value}
                    </option>
                  )
                ))}
              </select>
            ) : (
              <Input
                label="County / State"
                className="bg-main text-white border-light-grey md:text-lg"
                labelProps={{ className: "!text-white" }}
                value={formData.address4 ?? ""}
                onChange={(e) => ctrl.setValue("address4", e.target.value)}
                error={ctrl.address4Error}
              />
            )}

            <Input
              label="Postcode"
              className="bg-main text-white border-light-grey md:text-lg"
              labelProps={{ className: "!text-white" }}
              value={formData.postCode}
              onChange={(e) => ctrl.setValue("postCode", e.target.value)}
              error={ctrl.postCodeError}
              required
            />

            <Input
              label="Phone"
              className="bg-main text-white border-light-grey md:text-lg"
              labelProps={{ className: "!text-white" }}
              value={formData.phoneDay || ""}
              onChange={(e) => ctrl.setValue("phoneDay", e.target.value)}
              error={ctrl.phoneError}
              required
            />
          </div>
        )}
      </div>

      <div className="pt-2 pb-2 md:pt-4 md:pb-4 overflow-y-hidden flex-none border-t border-light-grey/40 flex justify-center md:justify-end items-center gap-3">
        <Button variant="text" color="red" size="lg" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          size="lg"
          className="bg-purple"
          onClick={handleSave}
          disabled={ctrl.isSaveDisabled || submitting}
        >
          {submitting ? <Spinner /> : "Save"}
        </Button>
      </div>
    </TwDialog>
  );
});
